/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */


import dayjs from "dayjs";
import { formatNumber } from "./numberFormatter";

export const ALL_FILTER_VALUE = "All";

export const PERQ_REPORT_DATE_FILTERS = [
  "Today",
  "Yesterday",
  "This Week",
  "Last 7 Days",
  "This Month",
  "Last Month",
  "Custom Date",
];

export const PERQ_DISCOUNT_CLASSES = ["managed", "unmanaged", "unclassified"];
export const PERQ_DISCOUNT_CLASS_LABELS = {
  managed: "Managed",
  unmanaged: "Unmanaged",
  unclassified: "Unclassified",
};
// Badge for each discount name option, keyed by the dropdown's `status`.
export const DISCOUNT_NAME_STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  both: "Both",
  no_perq_link: "No Perq",
};
export const DISCOUNT_NAME_STATUS_HINTS = {
  active: "Configured Perq, active in Toast",
  inactive: "Configured Perq, turned off or deleted in Toast",
  both: "Active and inactive Perqs share this name",
  no_perq_link: "Applied without a discount ID, so no Perq is linked",
};
export const PERQ_CHECK_IN_OPTIONS = ["with", "without"];
export const PERQ_ORDER_TYPES = ["all", "ot", "non_ot"];

export const DEFAULT_SORT_BY = "date";
export const DEFAULT_SORT_ORDER = "desc";

export const formatMoney = (value) => `$${formatNumber(value)}`;
export const formatPercent = (value) =>
  value === null || value === undefined ? "-" : `${Number(value).toFixed(1)}%`;

const normalizeDateValue = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs?.(value)) return value;

  if (value?.$d) {
    const reconstructedValue = dayjs(value.$d);
    return reconstructedValue.isValid() ? reconstructedValue : null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue : null;
};

const sanitizeStringList = (values, allowed) =>
  (Array.isArray(values) ? values : [])
    .filter((value) => allowed.includes(value));

// Unique, non-blank discount names.
const sanitizeNameList = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : []).filter(
      (value) => typeof value === "string" && value.trim() !== "",
    ),
  ),
];

export const defaultPerqFilters = () => ({
  dateFilter: "Today",
  customDateFrom: dayjs(),
  customDateTo: dayjs(),
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_sub_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
  discounts: [],
  discount_names: [],
  check_in: ALL_FILTER_VALUE,
  order_type: "all",
  search: "",
});

export const resolveDateRange = (filters) => {
  const today = dayjs();
  const customDateFrom = normalizeDateValue(filters?.customDateFrom);
  const customDateTo = normalizeDateValue(filters?.customDateTo);

  if (filters?.dateFilter === "Custom Date") {
    if (customDateFrom?.isValid?.() && customDateTo?.isValid?.()) {
      return {
        from: customDateFrom.format("DD/MM/YYYY"),
        to: customDateTo.format("DD/MM/YYYY"),
      };
    }
    return null;
  }

  switch (filters?.dateFilter) {
    case "Today":
      return { from: today.format("DD/MM/YYYY"), to: today.format("DD/MM/YYYY") };
    case "Yesterday": {
      const yesterday = today.subtract(1, "day");
      return {
        from: yesterday.format("DD/MM/YYYY"),
        to: yesterday.format("DD/MM/YYYY"),
      };
    }
    case "This Week":
      return {
        from: today.startOf("week").format("DD/MM/YYYY"),
        to: today.endOf("week").format("DD/MM/YYYY"),
      };
    case "Last 7 Days":
      return {
        from: today.subtract(7, "day").format("DD/MM/YYYY"),
        to: today.format("DD/MM/YYYY"),
      };
    case "This Month":
      return {
        from: today.startOf("month").format("DD/MM/YYYY"),
        to: today.endOf("month").format("DD/MM/YYYY"),
      };
    case "Last Month":
      return {
        from: today.subtract(1, "month").startOf("month").format("DD/MM/YYYY"),
        to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
      };
    default:
      return null;
  }
};

export const PERQ_SUMMARY_CLICKABLE_CARDS = [
  "managed",
  "unmanaged",
  "unclassified",
  "managed_without_check_in",
];

// The Managed w/o check-in tile is exactly the Managed classification with Without Check-in.
const isManagedWithoutCheckIn = (filters) => {
  const applied = sanitizeStringList(filters?.discounts, PERQ_DISCOUNT_CLASSES);
  return applied.length === 1 && applied[0] === "managed" && filters?.check_in === "without";
};

// Tile drill-downs reset the classification, so selected discount names are cleared too.
export const getFiltersForSummaryCard = (currentFilters, cardKey) => {
  const base = { ...currentFilters, discount_names: [] };
  const managedWithoutCheckIn = isManagedWithoutCheckIn(currentFilters);

  if (cardKey === "managed_without_check_in") {
    return {
      ...base,
      discounts: managedWithoutCheckIn ? [] : ["managed"],
      check_in: managedWithoutCheckIn ? ALL_FILTER_VALUE : "without",
    };
  }

  if (PERQ_DISCOUNT_CLASSES.includes(cardKey)) {
    // Leaving the Managed w/o check-in tile also drops its check-in filter.
    if (managedWithoutCheckIn) return { ...base, discounts: [cardKey], check_in: ALL_FILTER_VALUE };

    const applied = Array.isArray(currentFilters?.discounts) ? currentFilters.discounts : [];
    const isOnlyThisCard = applied.length === 1 && applied[0] === cardKey;
    return {
      ...base,
      discounts: isOnlyThisCard ? [] : [cardKey],
    };
  }

  return { ...currentFilters };
};

// The one classification selected in the filters, or null when none or several are.
export const getSingleDiscountClass = (filters) => {
  const applied = sanitizeStringList(filters?.discounts, PERQ_DISCOUNT_CLASSES);
  return applied.length === 1 ? applied[0] : null;
};

export const getSelectedSummaryCard = (filters) => {
  if (isManagedWithoutCheckIn(filters)) return "managed_without_check_in";
  return getSingleDiscountClass(filters);
};

export const isTipCompliant = (tipPercent, thresholdPercent) => {
  const tip = Number(tipPercent);
  const threshold = Number(thresholdPercent);
  if (tipPercent === null || tipPercent === undefined || Number.isNaN(tip)) return false;
  if (thresholdPercent === null || thresholdPercent === undefined || Number.isNaN(threshold)) {
    return false;
  }
  return tip >= threshold;
};

export const getPerqReportApiFilters = (filters) => {
  const params = {};

  const dateRange = resolveDateRange(filters);
  if (dateRange) {
    params.date_range = dateRange;
  }

  if (
    filters?.restaurant_group_id &&
    filters.restaurant_group_id !== ALL_FILTER_VALUE
  ) {
    params.restaurant_group_id = filters.restaurant_group_id;
  }

  if (
    filters?.restaurant_sub_group_id &&
    filters.restaurant_sub_group_id !== ALL_FILTER_VALUE
  ) {
    params.restaurant_sub_group_id = filters.restaurant_sub_group_id;
  }

  if (
    Array.isArray(filters?.restaurant_ids) &&
    !filters.restaurant_ids.includes(ALL_FILTER_VALUE)
  ) {
    params.restaurant_ids = filters.restaurant_ids;
  }

  const discounts = sanitizeStringList(filters?.discounts, PERQ_DISCOUNT_CLASSES);
  if (discounts.length > 0) {
    params.discounts = discounts;
  }

  const discountNames = sanitizeNameList(filters?.discount_names);
  if (discountNames.length > 0) {
    params.discount_names = discountNames;
  }

  if (PERQ_CHECK_IN_OPTIONS.includes(filters?.check_in)) {
    params.check_in = filters.check_in;
  }

  if (
    filters?.order_type &&
    filters.order_type !== "all" &&
    PERQ_ORDER_TYPES.includes(filters.order_type)
  ) {
    params.order_type = filters.order_type;
  }

  const searchTerm = `${filters?.search || ""}`.trim();
  if (searchTerm) {
    params.search = searchTerm;
  }

  return params;
};

// Restaurant scope and classification that the discount name options follow.
export const getDiscountNameOptionParams = (filters) => {
  const { restaurant_group_id, restaurant_sub_group_id, restaurant_ids, discounts } =
    getPerqReportApiFilters(filters);
  return { restaurant_group_id, restaurant_sub_group_id, restaurant_ids, discounts };
};

export const getDiscountNamesDisplayValue = (names) => {
  const selected = sanitizeNameList(names);
  if (selected.length === 0) return "All discount names";
  if (selected.length === 1) return selected[0];
  return `${selected.length} discount names selected`;
};
